import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
    CheckCircle2,
    Loader2,
    Sprout,
    MapPin,
    ShieldCheck,
    Sparkles,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { completeProfile } from "../lib/api";
import { LOGO_URL, BRAND } from "../lib/brand";

const EASTON_PARK_ZIPS = ["78744"];

const ProfileSetup = () => {
    const { user, profileComplete, loading, refresh } = useAuth();
    const navigate = useNavigate();
    const [address, setAddress] = useState("");
    const [zipCode, setZipCode] = useState("");
    const [acceptTerms, setAcceptTerms] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [waitlistMsg, setWaitlistMsg] = useState(null);

    useEffect(() => {
        if (loading) return;
        if (!user) {
            navigate("/login", { replace: true });
        } else if (profileComplete) {
            navigate("/", { replace: true });
        }
    }, [user, profileComplete, loading, navigate]);

    const zipOk = EASTON_PARK_ZIPS.includes(zipCode.trim());
    const canSubmit =
        address.trim().length >= 4 &&
        zipCode.trim().length >= 4 &&
        acceptTerms &&
        !submitting;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setWaitlistMsg(null);
        try {
            setSubmitting(true);
            await completeProfile({
                address: address.trim(),
                zipCode: zipCode.trim(),
                acceptTerms: true,
            });
            await refresh();
            toast.success("Welcome to Lendly!", {
                description: "Your profile is set up. Next: browse items to borrow, or head to Lending → 'List an item' to lend something out. Set up automatic payouts (green banner on Lending) before accepting rental requests.",
                duration: 9000,
            });
            navigate("/", { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail || "Couldn't save your profile.";
            if (err?.response?.status === 403) {
                setWaitlistMsg(detail);
            } else {
                toast.error(detail);
            }
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <main
            data-testid="profile-setup-page"
            className="relative z-10 overflow-hidden px-6 py-14 md:px-10 md:py-20"
        >
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -top-24 -right-16 h-[380px] w-[380px] rounded-full bg-[#737a3d]/15 blur-3xl"
            />
            <div
                aria-hidden="true"
                className="pointer-events-none absolute -bottom-24 -left-16 h-[320px] w-[320px] rounded-full bg-[#B56C5A]/10 blur-3xl"
            />

            <div className="relative mx-auto grid max-w-5xl items-center gap-10 lg:grid-cols-[0.9fr_1.1fr]">
                {/* Brand side */}
                <section className="animate-fade-up">
                    <div className="mb-4 inline-flex items-center gap-2 rounded-full bg-[#737a3d]/10 px-3 py-1.5">
                        <Sparkles className="h-3.5 w-3.5 text-[#737a3d]" strokeWidth={2.5} />
                        <span className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                            One last step
                        </span>
                    </div>
                    <h1 className="font-display text-4xl font-bold leading-[1.05] tracking-tight text-[#2e3220] sm:text-5xl">
                        Join your{" "}
                        <span className="relative inline-block">
                            <span className="relative z-10 text-[#737a3d]">verified neighborhood</span>
                            <span
                                aria-hidden="true"
                                className="absolute inset-x-0 bottom-1 h-3 rounded-full bg-[#8a9450]/30"
                            />
                        </span>
                    </h1>
                    <p className="mt-3 text-base leading-relaxed text-[#5a6142]">
                        We verify every member&apos;s address so {BRAND.name} stays a real
                        neighborhood network — not a stranger marketplace.
                    </p>

                    <div className="mt-7 hidden lg:block">
                        <div className="relative aspect-square max-w-xs">
                            <div className="absolute inset-0 rounded-[32px] border border-[#2e3220]/10 bg-gradient-to-br from-[#E8E2D0] to-[#d5cfb8]" />
                            <div className="absolute inset-0 flex items-center justify-center p-8">
                                <img
                                    src={LOGO_URL}
                                    alt="Lendly logo"
                                    className="h-full w-full object-contain"
                                />
                            </div>
                        </div>
                    </div>
                </section>

                {/* Form side */}
                <section className="animate-fade-up [animation-delay:120ms]">
                    <div className="rounded-3xl border border-[#2e3220]/10 bg-[#F4F1EA] p-7 shadow-[0_20px_60px_rgba(46,50,32,0.10)] md:p-9">
                        <Link
                            to="/"
                            className="mb-5 inline-flex items-center gap-2 lg:hidden"
                        >
                            <img
                                src={LOGO_URL}
                                alt="Lendly"
                                className="h-10 w-10 rounded-xl object-cover"
                            />
                            <span className="font-display text-2xl font-bold text-[#2e3220]">
                                {BRAND.name}
                            </span>
                        </Link>

                        <p className="text-xs font-bold uppercase tracking-widest text-[#737a3d]">
                            Set up your profile
                        </p>
                        <h2 className="mt-1 font-display text-2xl font-bold text-[#2e3220] sm:text-3xl">
                            Become a Lendly Neighbor
                        </h2>
                        {user?.name && (
                            <p className="mt-1 text-sm text-[#5a6142]">
                                Signed in as <span className="font-semibold">{user.name}</span>
                            </p>
                        )}

                        {waitlistMsg && (
                            <div
                                data-testid="profile-waitlist"
                                className="mt-5 rounded-2xl border border-[#B56C5A]/40 bg-[#B56C5A]/10 p-4 text-sm text-[#2e3220]"
                            >
                                <p className="font-display text-base font-bold text-[#B56C5A]">
                                    We&apos;re not on your block yet
                                </p>
                                <p className="mt-2 leading-relaxed">{waitlistMsg}</p>
                            </div>
                        )}

                        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
                            <label className="block">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                    Street address
                                </span>
                                <div className="mt-1 flex items-center gap-2 rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 focus-within:ring-2 focus-within:ring-[#737a3d]">
                                    <MapPin className="h-4 w-4 text-[#737a3d]" />
                                    <input
                                        type="text"
                                        value={address}
                                        onChange={(e) => setAddress(e.target.value)}
                                        data-testid="profile-address-input"
                                        placeholder="7900 Easton Park Blvd"
                                        className="w-full bg-transparent text-sm font-medium text-[#2e3220] placeholder:text-[#5a6142]/60 focus:outline-none"
                                    />
                                </div>
                            </label>
                            <label className="block">
                                <span className="text-[10px] font-bold uppercase tracking-widest text-[#737a3d]">
                                    ZIP code
                                </span>
                                <input
                                    type="text"
                                    inputMode="numeric"
                                    maxLength={10}
                                    value={zipCode}
                                    onChange={(e) => setZipCode(e.target.value)}
                                    data-testid="profile-zip-input"
                                    placeholder="78744"
                                    className="mt-1 w-full rounded-xl border border-[#2e3220]/20 bg-white/70 px-3 py-3 text-sm font-medium text-[#2e3220] placeholder:text-[#5a6142]/60 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[#737a3d]"
                                />
                                <p className="mt-1 inline-flex items-center gap-1 text-[11px] text-[#5a6142]">
                                    <Sprout className="h-3 w-3 text-[#737a3d]" />
                                    Currently serving Easton Park (78744). More neighborhoods soon.
                                </p>
                            </label>

                            <label className="flex items-start gap-3 rounded-2xl border border-[#2e3220]/10 bg-[#E8E2D0]/50 p-4">
                                <input
                                    type="checkbox"
                                    checked={acceptTerms}
                                    onChange={(e) => setAcceptTerms(e.target.checked)}
                                    data-testid="profile-terms-checkbox"
                                    className="mt-0.5 h-4 w-4 cursor-pointer accent-[#737a3d]"
                                />
                                <span className="text-sm leading-relaxed text-[#2e3220]">
                                    I&apos;ve read and agree to Lendly&apos;s{" "}
                                    <Link
                                        to="/terms"
                                        target="_blank"
                                        rel="noreferrer"
                                        className="font-semibold text-[#737a3d] hover:underline"
                                    >
                                        Community Terms & Agreement
                                    </Link>
                                    . I&apos;ll treat my neighbors and their gear with care.
                                </span>
                            </label>

                            <button
                                type="submit"
                                disabled={!canSubmit}
                                data-testid="profile-submit-btn"
                                className="btn-primary w-full disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {submitting ? (
                                    <>
                                        <Loader2 className="h-4 w-4 animate-spin" />
                                        Setting up your profile…
                                    </>
                                ) : (
                                    <>
                                        <CheckCircle2 className="h-4 w-4" strokeWidth={2.5} />
                                        Join the neighborhood
                                    </>
                                )}
                            </button>

                            {zipCode && !zipOk && (
                                <p className="text-center text-[11px] text-[#B56C5A]">
                                    That ZIP is outside Easton Park. We&apos;re only verifying
                                    78744 right now.
                                </p>
                            )}

                            <p className="inline-flex items-center justify-center gap-1.5 text-center text-[11px] text-[#5a6142] w-full">
                                <ShieldCheck className="h-3 w-3 text-[#737a3d]" />
                                Your exact address is only shared after a rental is confirmed.
                            </p>
                        </form>
                    </div>
                </section>
            </div>
        </main>
    );
};

export default ProfileSetup;
